import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, Settings, Shield, BarChart3, Target, BanIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  CookiePreferences,
  OPEN_SETTINGS_EVENT,
  getConsentDate,
  getPreferences,
  hasDecided,
  isTrackingOptedOut,
  revokeConsent,
  setTrackingOptOut,
  setPreferences as persistPreferences,
} from "@/lib/consent";

const optoutTexts: Record<string, { label: string; desc: string; active: string; on: string; off: string }> = {
  de: {
    label: "Download-Tracking dauerhaft deaktivieren (Opt-out)",
    desc: "Aktivieren Sie diesen Schalter, um die Download-Statistik und die Zuordnung von Downloads zu Anfragen für diesen Browser dauerhaft abzuschalten. Ihr pseudonymes Kennzeichen wird gelöscht und sofort aus allen bereits gespeicherten Zuordnungsdaten entfernt. Die Einstellung bleibt auch dann bestehen, wenn Sie Analyse-Cookies später erneut zulassen.",
    active: "Opt-out ist aktiv – es werden keine Downloads gezählt und keine Zuordnung gespeichert.",
    on: "Opt-out aktivieren",
    off: "Opt-out aufheben",
  },
  en: {
    label: "Permanently disable download tracking (opt-out)",
    desc: "Turn this on to permanently disable download statistics and download-to-enquiry attribution for this browser. Your pseudonymous marker is deleted and immediately removed from all stored attribution data. The setting persists even if you later allow analytics cookies again.",
    active: "Opt-out is active – no downloads are counted and no attribution is stored.",
    on: "Activate opt-out",
    off: "Cancel opt-out",
  },
  fr: {
    label: "Désactiver définitivement le suivi des téléchargements (opt-out)",
    desc: "Activez cette option pour désactiver définitivement les statistiques de téléchargement et l'attribution des téléchargements aux demandes pour ce navigateur. Votre identifiant pseudonyme est supprimé et immédiatement retiré de toutes les données d'attribution enregistrées. Le réglage est conservé même si vous autorisez à nouveau les cookies d'analyse.",
    active: "L'opt-out est actif – aucun téléchargement n'est compté, aucune attribution n'est enregistrée.",
    on: "Activer l'opt-out",
    off: "Annuler l'opt-out",
  },
  nl: {
    label: "Downloadtracking permanent uitschakelen (opt-out)",
    desc: "Zet dit aan om de downloadstatistiek en de koppeling van downloads aan aanvragen voor deze browser permanent uit te schakelen. Uw pseudonieme markering wordt verwijderd en direct uit alle opgeslagen attributiegegevens gehaald. De instelling blijft behouden, ook als u later opnieuw analytische cookies toestaat.",
    active: "Opt-out is actief – er worden geen downloads geteld en geen koppeling opgeslagen.",
    on: "Opt-out activeren",
    off: "Opt-out opheffen",
  },
  it: {
    label: "Disattivare definitivamente il tracciamento dei download (opt-out)",
    desc: "Attiva questa opzione per disattivare in modo permanente le statistiche di download e il collegamento tra download e richieste per questo browser. Il tuo identificativo pseudonimo viene eliminato e rimosso immediatamente da tutti i dati di attribuzione salvati. L'impostazione resta valida anche se in seguito accetti di nuovo i cookie di analisi.",
    active: "L'opt-out è attivo – nessun download viene conteggiato e nessuna attribuzione salvata.",
    on: "Attiva opt-out",
    off: "Annulla opt-out",
  },
  es: {
    label: "Desactivar permanentemente el seguimiento de descargas (opt-out)",
    desc: "Active esta opción para desactivar de forma permanente las estadísticas de descargas y la atribución de descargas a solicitudes en este navegador. Su identificador pseudónimo se elimina y se retira de inmediato de todos los datos de atribución almacenados. El ajuste se mantiene incluso si más adelante vuelve a permitir cookies de análisis.",
    active: "El opt-out está activo: no se cuentan descargas ni se guarda ninguna atribución.",
    on: "Activar opt-out",
    off: "Cancelar opt-out",
  },
};


const translations = {
  de: {
    bannerTitle: "Diese Website verwendet Cookies",
    bannerText: "Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Nutzererfahrung zu bieten. Sie können Ihre Einwilligung jederzeit widerrufen oder anpassen. Weitere Informationen finden Sie in unserer",
    privacyLink: "Datenschutzerklärung",
    necessaryOnly: "Nur notwendige",
    settings: "Einstellungen",
    acceptAll: "Alle akzeptieren",
    save: "Auswahl speichern",
    dialogTitle: "Cookie-Einstellungen",
    dialogDesc: "Wählen Sie, welche Cookies Sie zulassen möchten. Notwendige Cookies können nicht deaktiviert werden.",
    alwaysActive: "Immer aktiv",
    necessaryLabel: "Notwendige Cookies",
    necessaryDesc: "Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden. Sie speichern beispielsweise Ihre Cookie-Einstellungen und Session-Daten.",
    functionalLabel: "Funktionale Cookies",
    functionalDesc: "Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung, wie z.B. Ihre Spracheinstellungen oder Ihre Login-Daten.",
    analyticsLabel: "Analyse-Cookies",
    analyticsDesc: "Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem Informationen anonym gesammelt und gemeldet werden (Google Analytics 4). Ebenfalls in dieser Kategorie: die serverseitige Download-Statistik (welches PDF, Sprache, Gerätetyp) und ein pseudonymes Besucherkennzeichen im lokalen Speicher, mit dem ein Download einer späteren Anfrage zugeordnet werden kann. Ohne Ihre Einwilligung wird kein Kennzeichen erzeugt und kein Download protokolliert; beim Widerruf wird es sofort gelöscht.",
    marketingLabel: "Marketing-Cookies",
    marketingDesc: "Diese Cookies werden verwendet, um Ihnen relevante Werbung anzuzeigen. Sie werden von Drittanbietern wie Facebook gesetzt (Facebook Pixel).",
    consentGiven: "Einwilligung erteilt am",
    withdraw: "Alle Einwilligungen widerrufen",
  },
  en: {
    bannerTitle: "This website uses cookies",
    bannerText: "We use cookies and similar technologies to provide you with the best possible user experience. You can withdraw or adjust your consent at any time. More information can be found in our",
    privacyLink: "Privacy Policy",
    necessaryOnly: "Necessary only",
    settings: "Settings",
    acceptAll: "Accept all",
    save: "Save selection",
    dialogTitle: "Cookie settings",
    dialogDesc: "Choose which cookies you want to allow. Necessary cookies cannot be disabled.",
    alwaysActive: "Always active",
    necessaryLabel: "Necessary cookies",
    necessaryDesc: "These cookies are required for the basic functions of the website and cannot be disabled. They store, for example, your cookie settings and session data.",
    functionalLabel: "Functional cookies",
    functionalDesc: "These cookies enable enhanced functionality and personalization, such as your language preferences or login data.",
    analyticsLabel: "Analytics cookies",
    analyticsDesc: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously (Google Analytics 4). Also in this category: the server-side download statistics (which PDF, language, device type) and a pseudonymous visitor marker in local storage that links a download to a later enquiry. Without your consent no marker is created and no download is logged; on withdrawal it is deleted immediately.",
    marketingLabel: "Marketing cookies",
    marketingDesc: "These cookies are used to show you relevant advertising. They are set by third parties such as Facebook (Facebook Pixel).",
    consentGiven: "Consent given on",
    withdraw: "Withdraw all consent",
  },
  fr: {
    bannerTitle: "Ce site utilise des cookies",
    bannerText: "Nous utilisons des cookies et des technologies similaires pour vous offrir la meilleure expérience utilisateur possible. Vous pouvez retirer ou ajuster votre consentement à tout moment. Plus d'informations dans notre",
    privacyLink: "Politique de confidentialité",
    necessaryOnly: "Nécessaires uniquement",
    settings: "Paramètres",
    acceptAll: "Tout accepter",
    save: "Enregistrer la sélection",
    dialogTitle: "Paramètres des cookies",
    dialogDesc: "Choisissez les cookies que vous souhaitez autoriser. Les cookies nécessaires ne peuvent pas être désactivés.",
    alwaysActive: "Toujours actif",
    necessaryLabel: "Cookies nécessaires",
    necessaryDesc: "Ces cookies sont indispensables aux fonctions de base du site et ne peuvent pas être désactivés. Ils enregistrent par exemple vos préférences de cookies et données de session.",
    functionalLabel: "Cookies fonctionnels",
    functionalDesc: "Ces cookies permettent des fonctionnalités étendues et la personnalisation, telles que vos préférences linguistiques ou vos données de connexion.",
    analyticsLabel: "Cookies d'analyse",
    analyticsDesc: "Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant et rapportant des informations de manière anonyme (Google Analytics 4). Également dans cette catégorie : les statistiques de téléchargement côté serveur (quel PDF, langue, type d'appareil) et un identifiant de visiteur pseudonyme dans le stockage local permettant de relier un téléchargement à une demande ultérieure. Sans votre consentement, aucun identifiant n'est créé et aucun téléchargement n'est enregistré ; en cas de retrait, il est supprimé immédiatement.",
    marketingLabel: "Cookies marketing",
    marketingDesc: "Ces cookies sont utilisés pour vous proposer des publicités pertinentes. Ils sont définis par des tiers comme Facebook (Facebook Pixel).",
    consentGiven: "Consentement donné le",
    withdraw: "Retirer tous les consentements",
  },
  nl: {
    bannerTitle: "Deze website gebruikt cookies",
    bannerText: "Wij gebruiken cookies en vergelijkbare technologieën om u de best mogelijke gebruikerservaring te bieden. U kunt uw toestemming op elk moment intrekken of aanpassen. Meer informatie vindt u in onze",
    privacyLink: "Privacyverklaring",
    necessaryOnly: "Alleen noodzakelijke",
    settings: "Instellingen",
    acceptAll: "Alles accepteren",
    save: "Selectie opslaan",
    dialogTitle: "Cookie-instellingen",
    dialogDesc: "Kies welke cookies u wilt toestaan. Noodzakelijke cookies kunnen niet worden uitgeschakeld.",
    alwaysActive: "Altijd actief",
    necessaryLabel: "Noodzakelijke cookies",
    necessaryDesc: "Deze cookies zijn vereist voor de basisfuncties van de website en kunnen niet worden uitgeschakeld. Ze slaan bijvoorbeeld uw cookie-instellingen en sessiegegevens op.",
    functionalLabel: "Functionele cookies",
    functionalDesc: "Deze cookies maken uitgebreide functionaliteit en personalisatie mogelijk, zoals uw taalinstellingen of inloggegevens.",
    analyticsLabel: "Analytische cookies",
    analyticsDesc: "Deze cookies helpen ons te begrijpen hoe bezoekers met onze website omgaan door anoniem informatie te verzamelen en te rapporteren (Google Analytics 4). Ook in deze categorie: de serverzijdige downloadstatistiek (welke PDF, taal, apparaattype) en een pseudonieme bezoekersmarkering in local storage waarmee een download aan een latere aanvraag kan worden gekoppeld. Zonder uw toestemming wordt geen markering aangemaakt en geen download vastgelegd; bij intrekking wordt deze direct verwijderd.",
    marketingLabel: "Marketingcookies",
    marketingDesc: "Deze cookies worden gebruikt om u relevante advertenties te tonen. Ze worden geplaatst door derden zoals Facebook (Facebook Pixel).",
    consentGiven: "Toestemming gegeven op",
    withdraw: "Alle toestemmingen intrekken",
  },
  it: {
    bannerTitle: "Questo sito web utilizza i cookie",
    bannerText: "Utilizziamo cookie e tecnologie simili per offrirti la migliore esperienza utente possibile. Puoi revocare o modificare il tuo consenso in qualsiasi momento. Maggiori informazioni nella nostra",
    privacyLink: "Informativa sulla privacy",
    necessaryOnly: "Solo necessari",
    settings: "Impostazioni",
    acceptAll: "Accetta tutti",
    save: "Salva selezione",
    dialogTitle: "Impostazioni dei cookie",
    dialogDesc: "Scegli quali cookie consentire. I cookie necessari non possono essere disattivati.",
    alwaysActive: "Sempre attivo",
    necessaryLabel: "Cookie necessari",
    necessaryDesc: "Questi cookie sono necessari per le funzioni di base del sito e non possono essere disattivati. Memorizzano ad esempio le tue impostazioni dei cookie e i dati di sessione.",
    functionalLabel: "Cookie funzionali",
    functionalDesc: "Questi cookie consentono funzionalità avanzate e personalizzazione, come le tue preferenze linguistiche o i dati di accesso.",
    analyticsLabel: "Cookie di analisi",
    analyticsDesc: "Questi cookie ci aiutano a capire come i visitatori interagiscono con il nostro sito web raccogliendo e riportando informazioni in forma anonima (Google Analytics 4). Anche in questa categoria: la statistica dei download lato server (quale PDF, lingua, tipo di dispositivo) e un identificativo pseudonimo del visitatore nel local storage che collega un download a una richiesta successiva. Senza il tuo consenso non viene creato alcun identificativo né registrato alcun download; in caso di revoca viene eliminato immediatamente.",
    marketingLabel: "Cookie di marketing",
    marketingDesc: "Questi cookie vengono utilizzati per mostrarti annunci pertinenti. Sono impostati da terze parti come Facebook (Facebook Pixel).",
    consentGiven: "Consenso fornito il",
    withdraw: "Revoca tutti i consensi",
  },
  es: {
    bannerTitle: "Este sitio web utiliza cookies",
    bannerText: "Utilizamos cookies y tecnologías similares para ofrecerle la mejor experiencia de usuario posible. Puede retirar o ajustar su consentimiento en cualquier momento. Más información en nuestra",
    privacyLink: "Política de privacidad",
    necessaryOnly: "Solo necesarias",
    settings: "Configuración",
    acceptAll: "Aceptar todas",
    save: "Guardar selección",
    dialogTitle: "Configuración de cookies",
    dialogDesc: "Elija qué cookies desea permitir. Las cookies necesarias no se pueden desactivar.",
    alwaysActive: "Siempre activas",
    necessaryLabel: "Cookies necesarias",
    necessaryDesc: "Estas cookies son necesarias para las funciones básicas del sitio y no se pueden desactivar. Almacenan, por ejemplo, sus preferencias de cookies y datos de sesión.",
    functionalLabel: "Cookies funcionales",
    functionalDesc: "Estas cookies permiten funcionalidades avanzadas y personalización, como sus preferencias de idioma o datos de inicio de sesión.",
    analyticsLabel: "Cookies analíticas",
    analyticsDesc: "Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web recopilando e informando información de forma anónima (Google Analytics 4). También en esta categoría: la estadística de descargas del lado del servidor (qué PDF, idioma, tipo de dispositivo) y un identificador de visitante pseudónimo en el almacenamiento local que vincula una descarga con una consulta posterior. Sin su consentimiento no se crea ningún identificador ni se registra ninguna descarga; al retirarlo se elimina de inmediato.",
    marketingLabel: "Cookies de marketing",
    marketingDesc: "Estas cookies se utilizan para mostrarle anuncios relevantes. Las establecen terceros como Facebook (Facebook Pixel).",
    consentGiven: "Consentimiento otorgado el",
    withdraw: "Retirar todos los consentimientos",
  },
};

export const CookieConsent = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const tOpt = optoutTexts[language] ?? optoutTexts.en;
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false,
  });
  const [consentDate, setConsentDate] = useState<string | null>(null);
  const [optedOut, setOptedOut] = useState(false);
  const [optoutBusy, setOptoutBusy] = useState(false);

  useEffect(() => {
    setPreferences(getPreferences());
    setConsentDate(getConsentDate());
    setOptedOut(isTrackingOptedOut());
    if (!hasDecided()) setShowBanner(true);

    const openSettings = () => {
      setPreferences(getPreferences());
      setConsentDate(getConsentDate());
      setOptedOut(isTrackingOptedOut());
      setShowSettings(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, openSettings);
  }, []);

  const toggleOptOut = async (enabled: boolean) => {
    setOptoutBusy(true);
    setOptedOut(enabled);
    await setTrackingOptOut(enabled);
    if (enabled) setPreferences((p) => ({ ...p, analytics: false }));
    setOptoutBusy(false);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    persistPreferences({
      functional: prefs.functional,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
    });
    setPreferences({ ...prefs, necessary: true });
    setShowBanner(false);
    setShowSettings(false);
    window.location.reload();
  };

  const acceptAll = () =>
    savePreferences({ necessary: true, functional: true, analytics: true, marketing: true });
  const acceptNecessaryOnly = () =>
    savePreferences({ necessary: true, functional: false, analytics: false, marketing: false });
  const saveCustomPreferences = () => savePreferences(preferences);
  const withdrawAll = () => {
    revokeConsent();
    setShowSettings(false);
    window.location.reload();
  };

  if (!showBanner && !showSettings) return null;


  return (
    <>
      {showBanner && (
      <div className="fixed bottom-0 left-0 right-0 z-50 max-h-[85dvh] overflow-y-auto p-3 sm:p-4 bg-card border-t border-border shadow-glow animate-slide-in-bottom">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4 flex-1">
              <div className="hidden w-12 h-12 rounded-xl bg-primary/10 sm:flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1 sm:mb-2">
                  {t.bannerTitle}
                </h3>
                <p className="text-xs leading-5 text-muted-foreground sm:text-sm sm:leading-relaxed">
                  {t.bannerText}{" "}
                  <a href="/datenschutz" className="text-primary hover:underline">
                    {t.privacyLink}
                  </a>.
                </p>
              </div>
            </div>

            <div className="grid min-w-0 w-full grid-cols-2 items-center gap-2 md:flex md:w-auto md:flex-wrap">
              <Button variant="outline" onClick={acceptNecessaryOnly} className="h-auto whitespace-normal border-border text-foreground hover:bg-secondary">
                {t.necessaryOnly}
              </Button>
              <Button variant="outline" onClick={() => setShowSettings(true)} className="h-auto whitespace-normal border-border text-foreground hover:bg-secondary">
                <Settings className="hidden w-4 h-4 mr-2 min-[360px]:block" />
                {t.settings}
              </Button>
              <Button onClick={acceptAll} className="col-span-2 h-auto whitespace-normal bg-primary text-primary-foreground shadow-glow hover:bg-primary-hover md:col-span-1">
                {t.acceptAll}
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Cookie className="w-6 h-6 text-primary" />
              {t.dialogTitle}
            </DialogTitle>
            <DialogDescription>{t.dialogDesc}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <Shield className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Label className="text-base font-semibold cursor-pointer">{t.necessaryLabel}</Label>
                      <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-0.5 rounded">
                        {t.alwaysActive}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{t.necessaryDesc}</p>
                  </div>
                </div>
                <Switch checked={true} disabled className="mt-1" />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <Settings className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <Label htmlFor="functional" className="text-base font-semibold cursor-pointer">{t.functionalLabel}</Label>
                    <p className="text-sm text-muted-foreground mt-1">{t.functionalDesc}</p>
                  </div>
                </div>
                <Switch id="functional" checked={preferences.functional} onCheckedChange={(c) => setPreferences({ ...preferences, functional: c })} className="mt-1" />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <BarChart3 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <Label htmlFor="analytics" className="text-base font-semibold cursor-pointer">{t.analyticsLabel}</Label>
                    <p className="text-sm text-muted-foreground mt-1">{t.analyticsDesc}</p>
                  </div>
                </div>
                <Switch id="analytics" checked={preferences.analytics && !optedOut} disabled={optedOut} onCheckedChange={(c) => setPreferences({ ...preferences, analytics: c })} className="mt-1" />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <Target className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <Label htmlFor="marketing" className="text-base font-semibold cursor-pointer">{t.marketingLabel}</Label>
                    <p className="text-sm text-muted-foreground mt-1">{t.marketingDesc}</p>
                  </div>
                </div>
                <Switch id="marketing" checked={preferences.marketing} onCheckedChange={(c) => setPreferences({ ...preferences, marketing: c })} className="mt-1" />
              </div>
            </div>

            <div className="rounded-xl border-2 border-destructive/40 bg-destructive/5 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <BanIcon className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <Label htmlFor="tracking-optout" className="text-base font-bold cursor-pointer">
                      {tOpt.label}
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">{tOpt.desc}</p>
                    {optedOut && (
                      <p className="text-sm font-semibold text-destructive mt-2">{tOpt.active}</p>
                    )}
                  </div>
                </div>
                <Switch
                  id="tracking-optout"
                  checked={optedOut}
                  disabled={optoutBusy}
                  onCheckedChange={toggleOptOut}
                  aria-label={optedOut ? tOpt.off : tOpt.on}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {consentDate && (
            <p className="text-xs text-muted-foreground">
              {t.consentGiven}: {new Date(consentDate).toLocaleString(language)}
            </p>
          )}

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2">
            {consentDate && (
              <Button variant="ghost" size="sm" onClick={withdrawAll} className="w-full sm:w-auto text-muted-foreground">{t.withdraw}</Button>
            )}
            <Button variant="outline" onClick={acceptNecessaryOnly} className="w-full sm:w-auto">{t.necessaryOnly}</Button>
            <Button onClick={saveCustomPreferences} className="w-full sm:w-auto bg-primary hover:bg-primary-hover">{t.save}</Button>
            <Button onClick={acceptAll} className="w-full sm:w-auto bg-primary hover:bg-primary-hover">{t.acceptAll}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
